import { Home, KeyRound, type LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useListingModeStore } from '@services/livability/store'
import type { ListingType } from '@services/livability/types'
import { trackEvent } from '@services/livability/analytics'
import { cn } from '@/lib/utils'

import { useApartmentFilters } from '../hooks/useApartmentFilters'
import { buildFiltersAppliedEventProps } from '../utils/apartmentFilterUrl'

const MODES: { value: ListingType; labelKey: string; icon: LucideIcon }[] = [
  { value: 'rent', labelKey: 'filters.listingTypeRent', icon: Home },
  { value: 'sale', labelKey: 'filters.listingTypeSale', icon: KeyRound }
]

function useIsApartmentListOrMapRoute(): boolean {
  const { pathname } = useLocation()
  return (
    /\/apartments$/.test(pathname) || pathname.includes('/apartments-map')
  )
}

export function ListingModeToggle({
  className,
  compact = false
}: {
  className?: string
  /** Smaller sizing to sit inside the filter bar card. */
  compact?: boolean
}) {
  const { t } = useTranslation('livability')
  const { pathname } = useLocation()
  const isListOrMap = useIsApartmentListOrMapRoute()
  const storeListingMode = useListingModeStore((s) => s.listingMode)
  const setListingMode = useListingModeStore((s) => s.setListingMode)
  const { filters, q, page, limit, listingType, setListingTypeInUrl } =
    useApartmentFilters()

  const activeMode = isListOrMap ? listingType : storeListingMode
  const analyticsSource = pathname.includes('/apartments-map') ? 'map' : 'list'

  const handleChange = (mode: ListingType) => {
    if (isListOrMap) {
      setListingTypeInUrl(mode)
      trackEvent(
        'filters_applied',
        buildFiltersAppliedEventProps(analyticsSource, mode, filters, {
          q,
          page,
          limit
        })
      )
      return
    }
    setListingMode(mode)
  }

  return (
    <div
      className={cn(
        'inline-flex w-fit items-center self-start bg-ink/[0.04]',
        compact ? 'h-7 rounded-[10px] p-[2px]' : 'rounded-full p-1',
        className
      )}
      role="group"
      aria-label={t('filters.keys.listing_type')}
    >
      {MODES.map(({ value, labelKey, icon: Icon }) => {
        const active = activeMode === value
        return (
          <button
            key={value}
            type="button"
            aria-pressed={active}
            onClick={() => handleChange(value)}
            className={cn(
              'inline-flex cursor-pointer appearance-none items-center border-0 font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
              compact
                ? 'h-6 gap-1 rounded-[8px] px-2.5 text-[13px]'
                : 'gap-1.5 rounded-full px-4 py-1.5 text-sm',
              active
                ? 'bg-primary text-primary-foreground shadow-[0_2px_8px_rgba(17,173,187,0.35)]'
                : 'bg-transparent text-ink/60 hover:text-ink'
            )}
          >
            <Icon className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
            {t(labelKey)}
          </button>
        )
      })}
    </div>
  )
}
